import React, { useState, useEffect } from "react";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import {
  faFlask,
  faMusic,
  faDumbbell,
  faBook,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Maximize2 } from "lucide-react";
import { doc, onSnapshot } from "firebase/firestore";
import Updates from "./Updates";
import { db } from "../config/firebaseConfig";
import { useGalleryImages } from "../hooks/useGalleryImages";

// Import assets
import intro from "../assets/intro-rsps.jpg";
import intro1 from "../assets/intro-1-rsps.jpg";
import intro2 from "../assets/intro-2-rsps.jpg";
import detailsRsps from "../assets/upclass8.jpeg";

import gi40 from "../assets/gi40.jpg";

import gv13 from "../assets/gi13.mp4";
const defaultCarouselSettings = {
  autoPlay: true,
  infiniteLoop: true,
  transitionTime: 900,
  interval: 5000,
  showIndicators: true,
  stopOnHover: false,
  swipeable: true,
  emulateTouch: true,
};
const defaultCarouselSlides = [
  { type: "video", url: gv13 },
  { type: "image", url: intro },
  { type: "image", url: gi40 },
  { type: "image", url: intro1 },
  { type: "image", url: intro2 },
];

const useVisibilityObserver = (selector) => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle("opacity-100", entry.isIntersecting);
          entry.target.classList.toggle("opacity-0", !entry.isIntersecting);
        });
      },
      { threshold: 0.8 },
    );

    document.querySelectorAll(selector).forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [selector]);
};

const isVideoUrl = (value) =>
  /\.(mp4|webm|mov|m4v)(\?|$)/i.test(String(value || ""));

const isLikelyBundledStaticUrl = (value) =>
  /^\/?static\/media\//i.test(String(value || "").trim());

const buildAssetLookup = () => {
  try {
    const assetsContext = require.context(
      "../assets",
      false,
      /\.(png|jpe?g|gif|webp|avif|mp4|webm|mov|m4v)$/i,
    );

    return assetsContext.keys().reduce((lookup, key) => {
      const normalizedName = String(key || "")
        .replace(/^\.\//, "")
        .toLowerCase();
      lookup[normalizedName] = assetsContext(key);
      return lookup;
    }, {});
  } catch {
    return {};
  }
};

const assetLookup = buildAssetLookup();

const getItemFileName = (item) => {
  if (!item || typeof item !== "object") {
    return "";
  }

  return String(
    item.fileName || item.filename || item.originalFileName || item.name || "",
  ).trim();
};

const resolveLocalAssetUrl = (item) => {
  const fileName = getItemFileName(item);
  if (!fileName) {
    return "";
  }

  return assetLookup[fileName.toLowerCase()] || "";
};

const extractMediaUrl = (item, options = {}) => {
  const { preferLocalForBundledStatic = true } = options;

  if (typeof item === "string") {
    return item;
  }

  if (!item || typeof item !== "object") {
    return "";
  }

  const primaryUrl =
    item.url ||
    item.imageUrl ||
    item.downloadURL ||
    item.downloadUrl ||
    item.src ||
    "";
  const localFallbackUrl = resolveLocalAssetUrl(item);

  if (!primaryUrl) {
    return localFallbackUrl;
  }

  if (preferLocalForBundledStatic && isLikelyBundledStaticUrl(primaryUrl)) {
    return localFallbackUrl || primaryUrl;
  }

  return primaryUrl;
};

export default function Home() {
  useVisibilityObserver(".program-card");
  const {
    images: galleryImages,
    sources: gallerySources,
    loading: galleryLoading,
    error: galleryError,
    errorDetails: galleryErrorDetails,
    docExists: galleryDocExists,
  } = useGalleryImages();

  const showGalleryDebug = "production";
  // || process.env.NODE_ENV !== "production";
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [carouselSettings, setCarouselSettings] = useState(
    defaultCarouselSettings,
  );
  const [carouselSlides, setCarouselSlides] = useState(defaultCarouselSlides);
  const [isCarouselVideoPlaying, setIsCarouselVideoPlaying] = useState(false);
  const [popupSettings, setPopupSettings] = useState({
    popupImageUrl: "",
    isEnabled: true,
  });

  useEffect(() => {
    if (!showGalleryDebug) {
      return;
    }

    // eslint-disable-next-line no-console
    console.log("[GalleryDebug] Snapshot", {
      galleryLoading,
      galleryError,
      galleryErrorDetails,
      galleryDocExists,
      resolvedSources: gallerySources,
      inaugurationCount: Array.isArray(galleryImages?.inauguration)
        ? galleryImages.inauguration.length
        : null,
      schoolLifeCount: Array.isArray(galleryImages?.schoolLife)
        ? galleryImages.schoolLife.length
        : null,
    });
  }, [
    showGalleryDebug,
    galleryLoading,
    galleryError,
    galleryErrorDetails,
    galleryDocExists,
    gallerySources,
    galleryImages,
  ]);

  useEffect(() => {
    const settingsRef = doc(db, "siteSettings", "homeCarousel");

    const unsubscribe = onSnapshot(
      settingsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const settingsData = snapshot.data();

          setCarouselSettings({
            autoPlay:
              typeof settingsData.autoPlay === "boolean"
                ? settingsData.autoPlay
                : defaultCarouselSettings.autoPlay,
            infiniteLoop:
              typeof settingsData.infiniteLoop === "boolean"
                ? settingsData.infiniteLoop
                : defaultCarouselSettings.infiniteLoop,
            transitionTime:
              typeof settingsData.transitionTime === "number"
                ? settingsData.transitionTime
                : defaultCarouselSettings.transitionTime,
            interval:
              typeof settingsData.interval === "number"
                ? settingsData.interval
                : defaultCarouselSettings.interval,
            showIndicators:
              typeof settingsData.showIndicators === "boolean"
                ? settingsData.showIndicators
                : defaultCarouselSettings.showIndicators,
            stopOnHover:
              typeof settingsData.stopOnHover === "boolean"
                ? settingsData.stopOnHover
                : defaultCarouselSettings.stopOnHover,
            swipeable:
              typeof settingsData.swipeable === "boolean"
                ? settingsData.swipeable
                : defaultCarouselSettings.swipeable,
            emulateTouch:
              typeof settingsData.emulateTouch === "boolean"
                ? settingsData.emulateTouch
                : defaultCarouselSettings.emulateTouch,
          });

          if (
            Array.isArray(settingsData.slides) &&
            settingsData.slides.length
          ) {
            const validSlides = settingsData.slides.filter(
              (slide) =>
                slide &&
                (slide.type === "image" || slide.type === "video") &&
                typeof slide.url === "string" &&
                slide.url.trim() !== "",
            );

            setCarouselSlides(
              validSlides.length ? validSlides : defaultCarouselSlides,
            );
          } else {
            setCarouselSlides(defaultCarouselSlides);
          }

          return;
        }

        setCarouselSettings(defaultCarouselSettings);
        setCarouselSlides(defaultCarouselSlides);
      },
      () => {
        setCarouselSettings(defaultCarouselSettings);
        setCarouselSlides(defaultCarouselSlides);
      },
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const popupRef = doc(db, "siteSettings", "updatesPopup");

    const unsubscribe = onSnapshot(
      popupRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setPopupSettings({
            popupImageUrl: data.popupImageUrl || "",
            isEnabled: data.isEnabled !== false,
          });
          return;
        }

        setPopupSettings({
          popupImageUrl: "",
          isEnabled: true,
        });
      },
      () => {
        setPopupSettings({
          popupImageUrl: "",
          isEnabled: true,
        });
      },
    );

    return () => unsubscribe();
  }, []);

  const popupImageSrc =
    popupSettings.isEnabled && popupSettings.popupImageUrl
      ? popupSettings.popupImageUrl
      : detailsRsps;

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
  };

  return (
    <div className="w-full">
      {/* Hero Carousel */}
      <section className="bg-white py-8 px-4 md:px-12">
        <div className="max-w-6xl mx-auto rounded-2xl shadow-lg overflow-hidden">
          <Carousel
            showThumbs={false}
            autoPlay={carouselSettings.autoPlay && !isCarouselVideoPlaying}
            infiniteLoop={carouselSettings.infiniteLoop}
            transitionTime={carouselSettings.transitionTime}
            interval={carouselSettings.interval}
            stopOnHover={carouselSettings.stopOnHover}
            swipeable={carouselSettings.swipeable}
            emulateTouch={carouselSettings.emulateTouch}
            showStatus={false}
            showIndicators={carouselSettings.showIndicators}
            onChange={() => setIsCarouselVideoPlaying(false)}
          >
            {carouselSlides.map((slide, idx) => (
              <div
                key={`${slide.type}-${idx}`}
                className="h-[180px] sm:h-[240px] md:h-[320px] lg:h-[400px] bg-black flex justify-center items-center overflow-hidden"
              >
                {slide.type === "video" ? (
                  <video
                    controls
                    className="w-full h-full object-contain rounded-md"
                    src={slide.url}
                    onPlay={() => setIsCarouselVideoPlaying(true)}
                    onPause={() => setIsCarouselVideoPlaying(false)}
                    onEnded={() => setIsCarouselVideoPlaying(false)}
                  />
                ) : (
                  <img
                    src={slide.url}
                    alt={`Slide ${idx + 1}`}
                    className="w-full h-full object-contain rounded-md"
                  />
                )}
              </div>
            ))}
          </Carousel>
        </div>
      </section>

      {/* Updates & Popup */}
      <section className="grid md:grid-cols-2 gap-4 p-6 bg-gray-100">
        <div>
          <Updates />
        </div>
        <div>
          <img
            src={popupImageSrc}
            alt="Popup"
            className="w-full rounded-lg shadow"
          />
        </div>
      </section>

      {/* Featured Programs */}
      <section className="py-20 bg-gradient-to-b from-white via-blue-50 to-blue-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-10 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-blue-600">
              Learning Paths
            </p>
            <h2 className="mt-3 text-4xl font-black tracking-tight text-[#12325b] sm:text-5xl">
              Featured Programs
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-6 text-gray-600 sm:text-base">
              A sharper look at the core experiences that shape student growth,
              creativity, and confidence.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
            {[
              {
                icon: faFlask,
                title: "STEM Program",
                desc: "Explore our science, technology, engineering, and math curriculum designed to ignite curiosity.",
              },
              {
                icon: faMusic,
                title: "Arts & Music",
                desc: "Unleash creativity with our vibrant arts and music programs.",
              },
              {
                icon: faDumbbell,
                title: "Sports & Fitness",
                desc: "Develop teamwork and physical fitness through our sports programs.",
              },
              {
                icon: faBook,
                title: "Library",
                desc: "Enhance your learning with our extensive collection of books and resources.",
              },
            ].map((program, idx) => (
              <div
                key={idx}
                className="program-card group relative overflow-hidden rounded-[1.75rem] border border-blue-100 bg-white p-6 shadow-[0_18px_50px_rgba(18,50,91,0.08)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_25px_70px_rgba(18,50,91,0.18)] opacity-0"
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-600 via-cyan-400 to-blue-400" />
                <div className="mb-5 inline-flex rounded-2xl bg-blue-50 p-3 text-blue-700 transition duration-300 group-hover:bg-blue-600 group-hover:text-white">
                  <FontAwesomeIcon icon={program.icon} size="2x" />
                </div>
                <h3 className="text-xl font-bold text-[#12325b]">
                  {program.title}
                </h3>
                <p className="mt-3 text-sm leading-6 text-gray-600">
                  {program.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-24 bg-gradient-to-b from-blue-50 to-blue-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-10 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-blue-600">
              Campus Gallery
            </p>
            <h2 className="mt-3 text-4xl font-black tracking-tight text-[#12325b] sm:text-5xl">
              Inauguration ceremony
              {/* {showGalleryDebug && (
                <span className="ml-3 inline-flex items-center rounded-full bg-slate-900 px-3 py-1 text-[11px] font-semibold tracking-wide text-white">
                  DBG
                </span>
              )} */}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-6 text-gray-600 sm:text-base">
              A visual record of the first moments, milestones, and celebrations
              that shaped our school journey.
            </p>
            {/* {showGalleryDebug && (
              <div className="mx-auto mt-4 max-w-3xl rounded-2xl border border-slate-200 bg-white p-4 text-left text-xs text-slate-800 shadow-sm">
                <p className="font-semibold">Firebase → Render Logs</p>
                <div className="mt-2 grid gap-1 sm:grid-cols-2">
                  <div>
                    <span className="font-semibold">status:</span>{" "}
                    {galleryLoading
                      ? "loading"
                      : galleryError
                        ? "error"
                        : galleryDocExists === false
                          ? "doc-missing"
                          : inaugurationDebug.receivedCount === 0
                            ? "empty"
                            : "ready"}
                  </div>
                  <div>
                    <span className="font-semibold">docExists:</span>{" "}
                    {String(galleryDocExists)}
                  </div>
                  <div className="sm:col-span-2">
                    <span className="font-semibold">firebaseField:</span>{" "}
                    {gallerySources?.inauguration || "-"}
                  </div>
                  <div className="sm:col-span-2">
                    <span className="font-semibold">error:</span>{" "}
                    {galleryError || "-"}
                  </div>
                  <div>
                    <span className="font-semibold">received:</span>{" "}
                    {inaugurationDebug.receivedCount}
                  </div>
                  <div>
                    <span className="font-semibold">usable:</span>{" "}
                    {inaugurationDebug.usableCount} (missing url:{" "}
                    {inaugurationDebug.missingUrlCount})
                  </div>
                </div>
                <details className="mt-3">
                  <summary className="cursor-pointer select-none font-semibold text-slate-900">
                    Sample items (first {inaugurationDebug.sample.length})
                  </summary>
                  <pre className="mt-2 max-h-72 overflow-auto rounded-xl bg-slate-950 p-3 text-[11px] leading-5 text-slate-100">
                    {JSON.stringify(inaugurationDebug.sample, null, 2)}
                  </pre>
                </details>
              </div>
            )} */}
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
            {galleryLoading ? (
              <div className="col-span-full rounded-3xl border border-blue-100 bg-white py-16 text-center text-gray-500 shadow-sm">
                Loading images...
              </div>
            ) : galleryImages.inauguration.length === 0 ? (
              <div className="col-span-full rounded-3xl border border-blue-100 bg-white py-16 text-center text-gray-500 shadow-sm">
                No images available
              </div>
            ) : (
              galleryImages.inauguration.map((img, idx) => {
                const imgUrl = extractMediaUrl(img);
                const declaredType =
                  typeof img === "object" && img
                    ? String(img.type || "").toLowerCase()
                    : "";
                const isVideo =
                  declaredType === "video" || isVideoUrl(String(imgUrl || ""));

                if (!imgUrl) {
                  return null;
                }
                return (
                  <div
                    key={idx}
                    className="group relative aspect-[4/5] overflow-hidden rounded-3xl cursor-pointer bg-[#12325b] shadow-[0_18px_50px_rgba(18,50,91,0.2)] transition-transform duration-500 hover:-translate-y-1"
                    onClick={() => setSelectedImage(imgUrl)}
                  >
                    {isVideo ? (
                      <video
                        src={imgUrl}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    ) : (
                      <img
                        src={imgUrl}
                        alt={`Gallery ${idx + 1}`}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#12325b] via-[#12325b]/10 to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-100">
                      <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                        <div className="mb-2 inline-flex rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-100 backdrop-blur">
                          Inauguration
                        </div>
                        <h3 className="text-lg font-semibold leading-tight">
                          Campus Moment
                        </h3>
                        <p className="mt-1 text-sm text-blue-100">
                          Life at our school
                        </p>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Maximize2 className="h-9 w-9 text-white opacity-0 drop-shadow-lg transition duration-300 group-hover:opacity-100" />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#12325b]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-10 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-blue-200">
              Campus Moments
            </p>
            <h2 className="mt-3 text-4xl font-black tracking-tight text-white sm:text-5xl">
              Our School Life
              {/* {showGalleryDebug && (
                <span className="ml-3 inline-flex items-center rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold tracking-wide text-white">
                  DBG
                </span>
              )} */}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-6 text-blue-100 sm:text-base">
              Everyday moments, activities, and celebrations captured with a
              more editorial, immersive feel.
            </p>
            {/* {showGalleryDebug && (
              <div className="mx-auto mt-4 max-w-3xl rounded-2xl border border-white/15 bg-white/10 p-4 text-left text-xs text-white/90 shadow-sm backdrop-blur">
                <p className="font-semibold text-white">
                  Firebase → Render Logs
                </p>
                <div className="mt-2 grid gap-1 sm:grid-cols-2">
                  <div>
                    <span className="font-semibold">status:</span>{" "}
                    {galleryLoading
                      ? "loading"
                      : galleryError
                        ? "error"
                        : galleryDocExists === false
                          ? "doc-missing"
                          : schoolLifeDebug.receivedCount === 0
                            ? "empty"
                            : "ready"}
                  </div>
                  <div>
                    <span className="font-semibold">docExists:</span>{" "}
                    {String(galleryDocExists)}
                  </div>
                  <div className="sm:col-span-2">
                    <span className="font-semibold">firebaseField:</span>{" "}
                    {gallerySources?.schoolLife || "-"}
                  </div>
                  <div className="sm:col-span-2">
                    <span className="font-semibold">error:</span>{" "}
                    {galleryError || "-"}
                  </div>
                  <div>
                    <span className="font-semibold">received:</span>{" "}
                    {schoolLifeDebug.receivedCount}
                  </div>
                  <div>
                    <span className="font-semibold">usable:</span>{" "}
                    {schoolLifeDebug.usableCount} (missing url:{" "}
                    {schoolLifeDebug.missingUrlCount})
                  </div>
                </div>
                <details className="mt-3">
                  <summary className="cursor-pointer select-none font-semibold text-white">
                    Sample items (first {schoolLifeDebug.sample.length})
                  </summary>
                  <pre className="mt-2 max-h-72 overflow-auto rounded-xl bg-black/50 p-3 text-[11px] leading-5 text-white/90">
                    {JSON.stringify(schoolLifeDebug.sample, null, 2)}
                  </pre>
                </details>
              </div>
            )} */}
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {galleryLoading ? (
              <div className="col-span-full rounded-3xl border border-blue-200/20 bg-white/5 py-16 text-center text-blue-100 shadow-sm backdrop-blur">
                Loading images...
              </div>
            ) : galleryImages.schoolLife.length === 0 ? (
              <div className="col-span-full rounded-3xl border border-blue-200/20 bg-white/5 py-16 text-center text-blue-100 shadow-sm backdrop-blur">
                No images available
              </div>
            ) : (
              galleryImages.schoolLife.map((img, idx) => {
                const imgUrl = extractMediaUrl(img);
                const declaredType =
                  typeof img === "object" && img
                    ? String(img.type || "").toLowerCase()
                    : "";
                const isVideo =
                  declaredType === "video" || isVideoUrl(String(imgUrl || ""));

                if (!imgUrl) {
                  return null;
                }
                return (
                  <div
                    key={idx}
                    className="group relative aspect-[4/5] overflow-hidden rounded-3xl cursor-pointer bg-white/5 shadow-[0_18px_50px_rgba(15,23,42,0.2)] transition-transform duration-500 hover:-translate-y-1"
                    onClick={() => setSelectedImage(imgUrl)}
                  >
                    {isVideo ? (
                      <video
                        controls
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                        src={imgUrl}
                      />
                    ) : (
                      <img
                        src={imgUrl}
                        alt={`School Life ${idx + 1}`}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#12325b] via-[#12325b]/10 to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-100">
                      <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                        <div className="mb-2 inline-flex rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-100 backdrop-blur">
                          School Life
                        </div>
                        <h3 className="text-lg font-semibold leading-tight">
                          Campus Memory
                        </h3>
                        <p className="mt-1 text-sm text-blue-100">
                          Tap to view larger
                        </p>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Maximize2 className="h-9 w-9 text-white opacity-0 drop-shadow-lg transition duration-300 group-hover:opacity-100" />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* {showGalleryDebug && (
        <section className="py-10 bg-slate-950 text-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="rounded-2xl border border-white/15 bg-white/5 p-5 backdrop-blur">
              <h3 className="text-lg font-bold">Gallery Debug</h3>
              <p className="mt-1 text-xs text-white/70">
                {debugGallery
                  ? "Enabled via ?debugGallery=1 (or ?debug=gallery)"
                  : "Enabled automatically in development mode"}
              </p>
              <pre className="mt-4 max-h-[520px] overflow-auto rounded-xl bg-black/40 p-4 text-[11px] leading-5 text-white/85">
                {JSON.stringify(
                  {
                    galleryLoading,
                    galleryError,
                    galleryErrorDetails,
                    galleryDocExists,
                    gallerySources,
                    inaugurationCount: Array.isArray(
                      galleryImages?.inauguration,
                    )
                      ? galleryImages.inauguration.length
                      : null,
                    schoolLifeCount: Array.isArray(galleryImages?.schoolLife)
                      ? galleryImages.schoolLife.length
                      : null,
                    inaugurationSample: Array.isArray(
                      galleryImages?.inauguration,
                    )
                      ? galleryImages.inauguration.slice(0, 3)
                      : galleryImages?.inauguration,
                    schoolLifeSample: Array.isArray(galleryImages?.schoolLife)
                      ? galleryImages.schoolLife.slice(0, 3)
                      : galleryImages?.schoolLife,
                  },
                  null,
                  2,
                )}
              </pre>
            </div>
          </div>
        </section>
      )} */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setSelectedImage(null)}
        >
          {isVideoUrl(selectedImage) ? (
            <video
              src={selectedImage}
              controls
              className="max-h-[90vh] max-w-[90vw] rounded-xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img
              src={selectedImage}
              alt="Selected"
              className="max-h-[90vh] max-w-[90vw] rounded-xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <button
            type="button"
            onClick={() => setSelectedImage(null)}
            className="absolute right-4 top-4 rounded-lg bg-white px-3 py-1 text-sm font-semibold text-gray-800 shadow"
          >
            Close
          </button>
        </div>
      )}

      {/* Contact Form */}
      <section className="py-20 bg-gradient-to-b from-white via-blue-50 to-blue-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid gap-8 overflow-hidden rounded-3xl border border-blue-100 bg-white shadow-[0_20px_60px_rgba(18,50,91,0.1)] lg:grid-cols-[0.9fr_1.1fr]">
            <div className="bg-[#12325b] p-8 text-white sm:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-blue-200">
                Contact
              </p>
              <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl">
                Join Our School Community
              </h2>
              <p className="mt-4 max-w-md text-sm leading-7 text-blue-100">
                Interested in learning more? Fill out the form to schedule a
                visit or apply. We’ll connect you with the right team.
              </p>

              <div className="mt-8 space-y-3 text-sm text-blue-100">
                <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                  Admissions support and campus visits
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                  Quick response for parents and guardians
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                  Personalized enquiry handling
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-10">
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-slate-950">
                  Send an enquiry
                </h3>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  Tell us a bit about yourself and we’ll get back to you.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <input
                    type="text"
                    name="name"
                    placeholder="Full name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full rounded-2xl border border-blue-100 bg-blue-50/50 px-4 py-3.5 outline-none transition placeholder:text-gray-500 focus:border-blue-500 focus:bg-white"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full rounded-2xl border border-blue-100 bg-blue-50/50 px-4 py-3.5 outline-none transition placeholder:text-gray-500 focus:border-blue-500 focus:bg-white"
                  />
                </div>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-blue-100 bg-blue-50/50 px-4 py-3.5 outline-none transition placeholder:text-gray-500 focus:border-blue-500 focus:bg-white"
                />
                <textarea
                  name="message"
                  placeholder="Message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full rounded-2xl border border-blue-100 bg-blue-50/50 px-4 py-3.5 outline-none transition placeholder:text-gray-500 focus:border-blue-500 focus:bg-white"
                />
                <button
                  type="submit"
                  className="inline-flex w-full items-center justify-center rounded-2xl bg-blue-600 px-5 py-3.5 font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-blue-700"
                >
                  Submit
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
